import Modal from 'react-bootstrap/Modal';
import { useEffect, useMemo, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DataTable from '../../components/dataTable';
import PrimaryButton from '../../components/button';
import TextField from '../../components/textFiled';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFetchMenuItemsQuery, useUpdateMenuItemMutation, useFetchCategoriesQuery, useUpdateCategoryMutation, useDeleteCategoryMutation } from '../../api';

const STORAGE_KEY_V2 = 'menuCategoriesV2';

type Props = {
  show: boolean;
  hide: () => void;
};

type Category = { id: string; name: string };

function readCategories(): Category[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_V2);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveCategories(cats: Category[]) {
  localStorage.setItem(STORAGE_KEY_V2, JSON.stringify(cats));
}

export default function ViewCategoriesModal({ show, hide }: Props) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState('');

  // API
  const { data: categoriesData } = useFetchCategoriesQuery('');
  const [updateCategory] = useUpdateCategoryMutation();
  const [deleteCategory] = useDeleteCategoryMutation();

  // optional migration on rename
  const { data: itemsData } = useFetchMenuItemsQuery('');
  const [updateItem] = useUpdateMenuItemMutation();

  useEffect(() => {
    if (show) {
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
      setEditingId(null);
      setEditingValue('');
    }
  }, [show, categoriesData]);

  const headers = useMemo(() => ([
    { label: 'Category Name', value: 'name' },
    { label: 'Actions', value: 'actions', isAction: true },
  ]), []);

  const data = useMemo(() => 
    categories.map((c: any) => {
      const cid = c.id || c._id;
      const cname = c.name;
      return {
        id: cid,
        name: editingId === cid ? (
          <TextField
            label='Category Name'
            value={editingValue}
            onChange={(e: any) => setEditingValue(e.target.value)}
            type='text'
          />
        ) : (
          <span>{cname}</span>
        ),
        actions: (
          <div className='flex items-center gap-3'>
            {editingId === cid ? (
              <PrimaryButton
                label='Update'
                onClick={async () => {
                  const newName = editingValue.trim();
                  if (!newName) { toast.error('Name is required'); return; }
                  const prevName = categories.find((x: any) => (x.id||x._id) === cid)?.name;
                  try {
                    await updateCategory({ id: cid, body: { name: newName } }).unwrap();
                    setEditingId(null);
                    setEditingValue('');
                    toast.success('Category updated');
                  } catch (e) {
                    toast.error('Update failed');
                    return;
                  }
                  // migrate items (optional)
                  if (prevName && itemsData && Array.isArray(itemsData)) {
                    const items = itemsData as any[];
                    const affected = items.filter(it => it.type === prevName);
                    for (const it of affected) {
                      try {
                        await updateItem({ id: it._id, body: { ...it, type: newName } }).unwrap();
                      } catch {}
                    }
                  }
                }}
              />
            ) : (
              <EditIcon style={{ cursor: 'pointer' }} onClick={() => { setEditingId(cid); setEditingValue(cname); }} />
            )}
            <DeleteIcon style={{ cursor: 'pointer', color: '#FF0000' }} onClick={async () => {
              try {
                await deleteCategory(cid).unwrap();
                toast.success('Category deleted');
              } catch (e) {
                toast.error('Delete failed');
              }
            }} />
          </div>
        )
      };
    })
  , [categories, editingId, editingValue, itemsData, updateItem, updateCategory, deleteCategory]);

  return (
    <Modal show={show} onHide={hide} size="lg" centered>
      <Modal.Body>
        <div className='customer-details-title-ctn'>
          <span className='customer-details-title'>Categories</span>
        </div>
        <div className='modal-details-content'>
          <DataTable headers={headers} data={data} />
          <div className='flex justify-end mt-4'>
            <button type='button' className='px-4 py-2 bg-gray-800 text-white rounded' onClick={hide}>Close</button>
          </div>
        </div>
      </Modal.Body>
      <ToastContainer position="top-right" autoClose={2500} hideProgressBar theme='light' />
    </Modal>
  );
}
